import { useSyncExternalStore } from "react";

import {
  buildConsentCookie,
  readConsentFromCookieString,
  type ConsentChoice,
} from "@/lib/consent/consent";

/**
 * Tiny client-side consent store (subscribe / get / set).
 *
 * Nothing is known on the server or during hydration (`ready: false`), so UI
 * that depends on consent renders only after the browser state has been read:
 * no hydration mismatch and no flash of the banner for returning visitors.
 */
export interface ConsentState {
  /** False on the server and during hydration. */
  ready: boolean;
  /** The visitor's stored choice, if any. */
  choice: ConsentChoice | null;
  /** The browser sends Global Privacy Control, which counts as a reject. */
  gpc: boolean;
  /** Analytics may run: an explicit opt-in and no GPC signal. */
  analytics: boolean;
  /** The banner was reopened via "Cookie settings". */
  settingsOpen: boolean;
}

const SERVER_STATE: ConsentState = Object.freeze({
  ready: false,
  choice: null,
  gpc: false,
  analytics: false,
  settingsOpen: false,
});

let state: ConsentState | null = null;
let settingsOpener: HTMLElement | null = null;
const listeners = new Set<() => void>();

function detectGlobalPrivacyControl(): boolean {
  if (typeof navigator === "undefined") return false;

  return (
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true
  );
}

function createState(
  choice: ConsentChoice | null,
  settingsOpen: boolean,
): ConsentState {
  const gpc = detectGlobalPrivacyControl();

  return {
    ready: true,
    choice,
    gpc,
    analytics: choice === "analytics" && !gpc,
    settingsOpen,
  };
}

function readBrowserState(settingsOpen = false): ConsentState {
  return createState(
    readConsentFromCookieString(document.cookie),
    settingsOpen,
  );
}

function isSameState(a: ConsentState, b: ConsentState): boolean {
  return (
    a.ready === b.ready &&
    a.choice === b.choice &&
    a.gpc === b.gpc &&
    a.analytics === b.analytics &&
    a.settingsOpen === b.settingsOpen
  );
}

function setState(next: ConsentState) {
  if (state && isSameState(state, next)) return;
  state = next;
  for (const listener of [...listeners]) listener();
}

/** Current consent state (a stable object until something changes). */
export function getConsentState(): ConsentState {
  if (typeof document === "undefined") return SERVER_STATE;
  if (!state) state = readBrowserState();

  return state;
}

export function getServerConsentState(): ConsentState {
  return SERVER_STATE;
}

/** Pick up a choice made in another tab when this one regains focus. */
function syncFromBrowser() {
  if (typeof document === "undefined") return;
  setState(readBrowserState(getConsentState().settingsOpen));
}

function onVisibilityChange() {
  if (document.visibilityState === "visible") syncFromBrowser();
}

export function subscribeToConsent(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1 && typeof window !== "undefined") {
    window.addEventListener("focus", syncFromBrowser);
    document.addEventListener("visibilitychange", onVisibilityChange);
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== "undefined") {
      window.removeEventListener("focus", syncFromBrowser);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    }
  };
}

function restoreFocus() {
  const opener = settingsOpener;

  settingsOpener = null;
  if (opener?.isConnected) opener.focus();
}

/** Store the visitor's choice for a year and notify subscribers. */
export function setConsent(choice: ConsentChoice): void {
  if (typeof document === "undefined") return;

  document.cookie = buildConsentCookie(choice, {
    secure: window.location.protocol === "https:",
  });
  // If cookies are blocked the choice still applies for this page view.
  setState(
    createState(readConsentFromCookieString(document.cookie) ?? choice, false),
  );
  restoreFocus();
}

/** Reopen the banner ("Cookie settings"); focus returns to `opener` later. */
export function openConsentSettings(opener?: HTMLElement | null): void {
  if (typeof document === "undefined") return;

  settingsOpener =
    opener ??
    (document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null);
  setState({ ...getConsentState(), settingsOpen: true });
}

export function closeConsentSettings(): void {
  if (typeof document === "undefined") return;

  setState({ ...getConsentState(), settingsOpen: false });
  restoreFocus();
}

/** Whether analytics may run right now (always false on the server). */
export function hasAnalyticsConsent(): boolean {
  return getConsentState().analytics;
}

/** React binding for the consent store. */
export function useConsent(): ConsentState {
  return useSyncExternalStore(
    subscribeToConsent,
    getConsentState,
    getServerConsentState,
  );
}

/** Forget cached state (tests only). */
export function resetConsentStore(): void {
  state = null;
  settingsOpener = null;
  listeners.clear();
  if (typeof window !== "undefined") {
    window.removeEventListener("focus", syncFromBrowser);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  }
}
